import { useEffect, useRef, useState } from 'react';
import { Mail } from 'lucide-react';
import { useLinkAccount, usePrivy, type User as PrivyUser } from '@privy-io/react-auth';
import Modal from '@/components/ui/Modal';
import { useAuthStore } from '@/store/authStore';
import { fetchProfile, getStoredToken, loginWithPrivy } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';

// Let the post-login transition settle before overlaying anything (the July
// consent-gate attempt showed login-path interruptions read as broken UI).
const OPEN_DELAY_MS = 2500;

/**
 * True when Privy already knows an email for this user, either as a linked
 * email account or through an OAuth account (Google, Apple, …) that carries one.
 */
function privyUserHasEmail(user: PrivyUser): boolean {
  if (user.email?.address) return true;
  return user.linkedAccounts.some(
    (account) => 'email' in account && Boolean((account as { email?: string | null }).email),
  );
}

/**
 * Post-login email capture for wallet-only sign-ins: once the backend session
 * is established, offer to link an email via Privy's own modal (`linkEmail`
 * handles input + OTP verification; the address lands on the Privy user).
 * Deliberately outside the login critical path: the app is fully usable
 * behind it and dismissing it never blocks anything.
 */
export function LinkEmailPrompt() {
  const { ready, authenticated, user, getAccessToken } = usePrivy();
  const isAppAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const setProfile = useAuthStore((s) => s.setProfile);
  const [open, setOpen] = useState(false);
  // Offer at most once per page load, even if the user abandons the Privy modal.
  const offeredRef = useRef(false);

  // The newly linked email lands on the Privy user only — our backend reads it
  // only during the /auth/privy exchange, and a restored session skips that
  // exchange for up to 7 days (see api.ts). Re-run it right after a successful
  // link so the backend/admin picks up the address immediately instead of
  // waiting for the next real sign-in. Best-effort and silent: never surfaces,
  // blocks, or otherwise changes app state on failure. This prompt is mounted
  // beside <App />, outside its QueryClientProvider, so it uses the shared
  // queryClient instance directly rather than the useQueryClient() hook.
  const resyncBackendEmail = async (): Promise<void> => {
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('No Privy session');
      await loginWithPrivy(token);
      const p = await fetchProfile();
      setProfile(p);
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    } catch (err) {
      console.warn('Post-link backend resync failed', err);
      // fetchProfile drops a rejected JWT; keep the store in line with it, as
      // fetchQuests does, instead of leaving a signed-in UI with no session.
      if (!getStoredToken()) {
        useAuthStore.getState().setAuthenticated(false);
        setProfile(null);
      }
    }
  };

  const { linkEmail } = useLinkAccount({
    onSuccess: ({ linkMethod, linkedAccount }) => {
      // Privy fires this for every link flow (wallet, socials, …), not just
      // email, so gate on both the method and the linked account's own type.
      if (linkMethod === 'email' && linkedAccount.type === 'email') {
        void resyncBackendEmail();
      }
    },
  });

  const shouldOffer =
    ready && authenticated && isAppAuthenticated && !!user && !privyUserHasEmail(user);

  useEffect(() => {
    if (!shouldOffer) {
      setOpen(false);
      return;
    }
    if (offeredRef.current) return;
    const timer = window.setTimeout(() => {
      offeredRef.current = true;
      setOpen(true);
    }, OPEN_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [shouldOffer]);

  if (!open) return null;

  // No persistence on dismissal: as long as no email is linked, the prompt
  // returns on every page load (at most once per load via offeredRef).
  const dismiss = () => setOpen(false);

  const startLink = () => {
    setOpen(false);
    linkEmail();
  };

  return (
    <Modal
      open={open}
      onClose={dismiss}
      labelledBy="link-email-title"
      className="max-w-md"
      showClose={false}
      dismissOnBackdrop={false}
    >
      <div className="p-6 sm:p-8">
        <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-water-50 text-water-600">
          <Mail className="h-5 w-5" aria-hidden="true" />
        </span>
        <h2
          id="link-email-title"
          className="mt-4 font-display text-2xl font-bold tracking-tightish text-ink-950"
        >
          One last step
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-slate2-600">
          Add your email and be the first to discover what makes the IRL7 membership so special.
        </p>
        <div className="mt-6">
          <button type="button" className="btn-water w-full" onClick={startLink}>
            Get the first look
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default LinkEmailPrompt;
